import { useMemo, useState, useRef } from 'react';
import { useAppContext } from 'services/context';
import { useToggle, useTranslation } from 'services/hooks';
import { IsMobile } from 'services/utils';
import TextArea from 'components/Input/TextArea';
import File from 'pages/Conversation/File/index.js';
import Icon from 'components/Icon';

import classNames from 'classnames';
import { ROLES, MESSAGE_STATUS, NOTIFICATION_TYPES } from 'constants';

import './style.css';

const Message = ({
	id,
	messageBody,
	messageStatusId,
	parentMessage,
	replayMessageOnClick,
	time,
	isOwnMessage,
	files,
	updateLocalMessage,
	scrollToMessage,
	markedMessage,
}) => {
	const { Api, user, addNotification } = useAppContext();
	const [isUpdateMessageTextMode, setIsUpdateMessageTextMode] = useToggle(false);
	const [updatedMessageText, setUpdatedMessageText] = useState(messageBody);

	const messageRef = useRef(null);
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'conversation';

	const className = classNames('Message', {
		sender: isOwnMessage,
		receiver: !isOwnMessage,
	});

	const massageContainer = classNames('Message__container', {
		has_file: files.length > 0,
		has_parent: parentMessage.parentMessageId,
		markedMessage: markedMessage && id === markedMessage,
	});

	const isTeacher = useMemo(() => {
		return user.roleId === ROLES.Teacher || user.roleId === ROLES.TeacherTeamLeader;
	}, [user]);

	const saveMessageEdition = async () => {
		if (!updatedMessageText) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_message_edition_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}

		try {
			const messageToUpdate = {
				messageId: id,
				messageBody: updatedMessageText,
				parentMessageId: null,
			};
			//if the message didnt changed we dont need to update
			if (updatedMessageText.trim() === '' || updatedMessageText.trim() === messageBody.trim()) return;

			const res = await Api.InsertUpdateConversationMessage(messageToUpdate);

			if (!res.messageId && !res.isUpdate) {
				addNotification(getTranslation(TRANSLATION_KEY, res.message), NOTIFICATION_TYPES.ERROR, 8000);

				return;
			}

			//Checks that updateLocalMessage is a function
			updateLocalMessage && typeof updateLocalMessage === 'function' && updateLocalMessage(res);
			setUpdatedMessageText(res.messageBody);
		} catch (err) {
			console.error('Update Message Failed', err);
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_message_update_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		} finally {
			setIsUpdateMessageTextMode(!isUpdateMessageTextMode);
		}
	};

	const seenIconProperties = useMemo(() => {
		if (messageStatusId === MESSAGE_STATUS.SENT) {
			return {
				iconName: 'sentMessageIcon',
				size: '11',
			};
		} else if (messageStatusId === MESSAGE_STATUS.SEEN) {
			return {
				iconName: 'seenMessageIcon',
			};
		}
	}, [messageStatusId]);

	const updateIconProperties = useMemo(() => {
		if (!isUpdateMessageTextMode && isOwnMessage) {
			return {
				iconName: 'editIcon',
				size: '10',
				title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_message_edit_icon_title`),
				color: 'white',
				className: 'Message__update',
				onClick: () => setIsUpdateMessageTextMode(!isUpdateMessageTextMode),
			};
		} else if (isUpdateMessageTextMode) {
			return {
				iconName: 'approvalIcon',
				color: 'white',
				title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_message_save_icon_title`),
				size: '15',
				className: 'Message__save',
				onClick: saveMessageEdition,
			};
		}
	}, [isUpdateMessageTextMode, isOwnMessage, updatedMessageText, getTranslation]);

	const _replayMessage = () => {
		//returns the message we clicked on
		replayMessageOnClick && typeof replayMessageOnClick === 'function' && replayMessageOnClick(id);
	};

	const handleScrollToMessage = () => {
		scrollToMessage && typeof scrollToMessage === 'function' && scrollToMessage(parentMessage.messageId);
	};

	return (
		<div className={className} m-id={id} ref={messageRef}>
			<div className="Message__time">{time}</div>
			<div className={massageContainer}>
				{parentMessage.messageId && Object.keys(parentMessage).length > 0 && (
					<div className="Message__container__parent">
						<ParentMessage
							id={parentMessage.messageId}
							owner={parentMessage.parentMessageOwner}
							message={parentMessage.messageBody}
							messageObj={parentMessage}
							onClickScroll={handleScrollToMessage}
						/>
					</div>
				)}
				<div className="Message__text" key={id}>
					{messageBody && !isUpdateMessageTextMode && (
						<span dangerouslySetInnerHTML={{ __html: messageBody.replace(/\n/g, '<br/>') }}></span>
					)}
					{messageBody && isUpdateMessageTextMode && (
						<TextArea defaultValue={messageBody} onChange={({ target }) => setUpdatedMessageText(target.value)} maxlength="1500" />
					)}
					{files && files.length > 0 && files.map((file, idx) => <File key={file?.fileName || '' + idx} file={file} />)}
					{files && files.length > 0 && (
						<div className="file_list__length">
							{files.length} {getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_message_count_files_note`)}
						</div>
					)}
				</div>
				<div className="Message__icon">
					<Icon icon={seenIconProperties?.iconName} color={updateIconProperties?.color} size={seenIconProperties?.size}></Icon>
				</div>
				{isTeacher && messageBody && (
					<div
						className={updateIconProperties?.className}
						title={updateIconProperties?.title}
						onClick={() => updateIconProperties?.onClick()}
					>
						<Icon icon={updateIconProperties?.iconName} color={updateIconProperties?.color} size={updateIconProperties?.size} />
					</div>
				)}
			</div>
			<span className="Message__replay" title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_message_replay_icon_title`)}>
				<Icon onClick={!IsMobile && _replayMessage} onTouchStart={IsMobile && _replayMessage} icon="replayIcon" color="#6b6b6b" size={17} />
			</span>
		</div>
	);
};

export default Message;

export const ParentMessage = ({ id, message, owner, cancelReplay, messageObj, onClickScroll }) => {
	const handleScrollOnClick = () => {
		onClickScroll && typeof onClickScroll === 'function' && onClickScroll(id);
	};

	const fileObj = useMemo(() => {
		if (!Object.keys(messageObj).length) return {};
		if (messageObj.hasFile) {
			return { fileData: messageObj.fileData, fileName: messageObj.fileName, fileSFix: messageObj.fileSFix };
		}
	}, [messageObj]);

	return (
		<div className="ParentMessage" key={id} onClick={handleScrollOnClick}>
			<div className="line"></div>
			<div className="ParentMessage__content">
				<div className="ParentMessage__owner">{owner}</div>
				<div className="ParentMessage__text">
					{(message && message) || (message && message.length > 50 && message.substring(0, 50) + '...')}
				</div>
				{fileObj && (
					<div className="ParentMessage__file">
						<File key={fileObj?.fileName || ''} file={fileObj || undefined} />
					</div>
				)}
			</div>
			{cancelReplay && <Icon icon="closeIcon" size="15" color="#707070" onClick={cancelReplay} />}
		</div>
	);
};
